import { useCallback, useState } from 'react'
import { useCreateTopic, useAddNoteTopic } from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { PrivacyType, TopicType } from '@/utils/constants'
import type { CreateGalleryFromAlbumResult, WizardDraft } from '../types'
import { buildGalleryPatternDraft, countSeededPrints } from '../utils/buildGalleryPatternDraft'
import { persistPatternNotes } from '../utils/persistPatternNotes'
import toast from 'react-hot-toast'

export type CreateGalleryPhase = 'idle' | 'creating' | 'seeding' | 'done' | 'error'

/**
 * Creates a GALLERY topic and seeds pattern notes (guest-flow equivalent of
 * createTopic + copyDraftToTopic) so the editor opens with prints applied.
 */
export function useCreateGalleryFromAlbum() {
  const [ewUser] = useLocalStorage('ewUser', null)
  const createTopic = useCreateTopic(ewUser?.userID || '')
  const addNote = useAddNoteTopic()
  const [saving, setSaving] = useState(false)
  const [phase, setPhase] = useState<CreateGalleryPhase>('idle')

  const save = useCallback(
    async (draft: WizardDraft): Promise<CreateGalleryFromAlbumResult | null> => {
      if (!ewUser?.userID) {
        toast.error('Sign in to create a gallery')
        return null
      }

      setSaving(true)
      setPhase('creating')

      try {
        const patternDraft = buildGalleryPatternDraft(draft)
        const printCount = countSeededPrints(patternDraft)

        if (printCount === 0) {
          toast.error('Include at least one photo before creating')
          setPhase('error')
          return null
        }

        const privacyType =
          draft.privacy === 'public' ? PrivacyType.PUBLIC : PrivacyType.PRIVATE

        const xData = JSON.stringify({
          sourceAlbumId: draft.albumId,
          sourceAlbumIds: [draft.albumId],
          seeded: true,
          printCount,
          wizard: {
            sceneId: draft.sceneId,
            frameId: draft.frameId,
            colorId: draft.colorId,
            materialId: draft.materialId,
            matWidth: draft.matWidth,
            matColor: draft.matColor,
            slots: draft.slots.filter((s) => s.photoId && !s.excluded),
          },
        })

        const result = await createTopic.mutateAsync({
          topicName: draft.galleryTitle.trim() || 'Gallery',
          topicType: TopicType.GALLERY.toString(),
          allowContribution: 0,
          topicXData: xData,
          privacyType,
          cliques: [],
          connections: [],
        })

        if (result?.isError) {
          throw new Error(result.errorMessage || 'Could not create gallery')
        }

        const galleryId = result.topicID as string

        setPhase('seeding')
        await persistPatternNotes(galleryId, patternDraft, (params) =>
          addNote.mutateAsync(params),
        )

        // One-shot handoff flag for editor toast
        try {
          sessionStorage.setItem(
            `ew-gallery-seeded:${galleryId}`,
            JSON.stringify({ printCount, title: draft.galleryTitle.trim() }),
          )
        } catch {
          /* ignore */
        }

        setPhase('done')
        toast.success(
          printCount === 1
            ? 'Gallery created — 1 photo placed'
            : `Gallery created — ${printCount} photos placed`,
        )
        return { galleryId, title: draft.galleryTitle.trim(), printCount }
      } catch (e) {
        setPhase('error')
        toast.error(e instanceof Error ? e.message : 'Could not create gallery')
        return null
      } finally {
        setSaving(false)
      }
    },
    [ewUser?.userID, createTopic, addNote],
  )

  return { save, saving, phase }
}
